// Global Instructions Rule Applied!
import React, { useState, useEffect } from 'react'
import { Modal, Space, message, Skeleton, Divider } from 'antd'
import Button from '../../../core/components/Button.jsx'
import { InputManager } from '../../../core/components/InputManager/InputManager.tsx'
import { FieldTypeEnum } from '../../../core/components/InputManager/types.ts'
import { TaskRecordFields } from '../../../state_manager/data_models/task_record.ts'
import { fetchTaskLinks, processTaskLinks } from '../services/tasksService.ts'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export function TaskLinkDelinkModal({ visible, task, onCancel, onSuccess, accessToken, triggerRef }) {
  const [loading, setLoading] = useState(false)
  const [loadingLinks, setLoadingLinks] = useState(false)
  const [modalStyle, setModalStyle] = useState({})
  const [fieldValues, setFieldValues] = useState({
    account_ids: [],
    deal_ids: [],
    prospect_ids: []
  })

  useEffect(() => {
    if (visible && task) {
      loadExistingLinks()
      updateModalPosition()

      const handleUpdatePosition = () => {
        if (visible) {
          updateModalPosition()
        }
      }

      window.addEventListener('scroll', handleUpdatePosition, true)
      window.addEventListener('resize', handleUpdatePosition)

      return () => {
        window.removeEventListener('scroll', handleUpdatePosition, true)
        window.removeEventListener('resize', handleUpdatePosition)
      }
    } else if (!visible) {
      setFieldValues({
        account_ids: [],
        deal_ids: [],
        prospect_ids: []
      })
    }
  }, [visible, task])

  const handleAfterClose = () => {
    setModalStyle({})
  }

  const updateModalPosition = () => {
    if (!triggerRef?.current) {
      setModalStyle({})
      return
    }

    const triggerElement = triggerRef.current
    const rect = triggerElement.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

    const modalWidth = 600
    const modalHeight = 400
    const gap = 8

    let top = rect.bottom + scrollTop + gap
    let left = rect.left + scrollLeft

    const windowWidth = window.innerWidth
    if (left + modalWidth > windowWidth) {
      left = windowWidth - modalWidth - 16
    }
    if (left < 16) {
      left = 16
    }

    const windowHeight = window.innerHeight
    const availableSpaceBelow = windowHeight - rect.bottom
    const availableSpaceAbove = rect.top

    if (availableSpaceBelow < modalHeight && availableSpaceAbove > modalHeight) {
      top = rect.top + scrollTop - modalHeight - gap
    } else if (availableSpaceBelow < modalHeight) {
      top = scrollTop + windowHeight - modalHeight - 16
    }

    setModalStyle({
      top: `${top}px`,
      left: `${left}px`,
      paddingBottom: 0,
      margin: 0,
      maxWidth: `${modalWidth}px`
    })
  }

  const loadExistingLinks = async () => {
    if (!task || !accessToken) return

    const taskId = task[TaskRecordFields.tasks__id]
    if (!taskId) return

    try {
      setLoadingLinks(true)
      const links = await fetchTaskLinks(taskId, accessToken)

      const accountIds = (links.account_ids || []).map((item) => ({
        value: typeof item === 'object' ? item.id : item,
        label: typeof item === 'object' ? item.label : item
      }))
      const dealIds = (links.deal_ids || []).map((item) => ({
        value: typeof item === 'object' ? item.id : item,
        label: typeof item === 'object' ? item.label : item
      }))
      const prospectIds = (links.prospect_ids || []).map((item) => ({
        value: typeof item === 'object' ? item.id : item,
        label: typeof item === 'object' ? item.label : item
      }))

      setFieldValues({
        account_ids: accountIds,
        deal_ids: dealIds,
        prospect_ids: prospectIds
      })
    } catch (error) {
      console.error('Error loading task links:', error)
      message.error('Failed to load existing links')
    } finally {
      setLoadingLinks(false)
    }
  }

  const handleSave = async () => {
    try {
      const taskId = task[TaskRecordFields.tasks__id]

      if (!taskId) {
        message.error('Task ID not found')
        return
      }

      setLoading(true)

      const accountIds = (fieldValues.account_ids || []).map((item) => {
        return typeof item === 'object' && item.value ? item.value : item
      })
      const dealIds = (fieldValues.deal_ids || []).map((item) => {
        return typeof item === 'object' && item.value ? item.value : item
      })
      const prospectIds = (fieldValues.prospect_ids || []).map((item) => {
        return typeof item === 'object' && item.value ? item.value : item
      })

      await processTaskLinks(
        {
          task_id: taskId,
          account_ids: accountIds,
          deal_ids: dealIds,
          prospect_ids: prospectIds
        },
        accessToken
      )

      message.success('Links updated successfully')
      onSuccess?.()
      onCancel()
    } catch (error) {
      console.error('Error saving task links:', error)
      message.error(error.message || 'Failed to save links')
    } finally {
      setLoading(false)
    }
  }

  const taskName = task?.[TaskRecordFields.tasks__subject] || 'Task'

  const modalTitle = (
    <span>
      Link/Delink: <span style={{ color: '#000000', fontWeight: 'bold' }}>{taskName}</span>
    </span>
  )

  const fields = [
    {
      key: 'account_ids',
      label: 'Accounts',
      type: FieldTypeEnum.COMPANY,
      value: fieldValues.account_ids,
      mode: 'multiple',
      placeholder: 'Search accounts...',
      required: false,
      autoFocus: true
    },
    {
      key: 'deal_ids',
      label: 'Deals',
      type: FieldTypeEnum.DEAL,
      value: fieldValues.deal_ids,
      mode: 'multiple',
      placeholder: 'Search deals...',
      required: false
    },
    {
      key: 'prospect_ids',
      label: 'Prospects',
      type: FieldTypeEnum.PROSPECT,
      value: fieldValues.prospect_ids,
      mode: 'multiple',
      placeholder: 'Search prospects...',
      required: false
    }
  ]

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      afterClose={handleAfterClose}
      closable={false}
      maskClosable={false}
      footer={
        <Space>
          <Button variant='cancel' onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            icon={<FontAwesomeIcon icon={faCheck} style={{ color: '#FFFFFF' }} />}
            variant='save'
            onClick={handleSave}
            loading={loading}
          >
            Save
          </Button>
        </Space>
      }
      width={600}
      style={Object.keys(modalStyle).length > 0 ? modalStyle : undefined}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }
      }}
    >
      <Divider style={{ margin: '16px 0' }} />
      {loadingLinks ? (
        <div className='flex flex-col gap-4'>
          <Skeleton.Input active size='small' style={{ width: '100%', height: '32px' }} />
          <Skeleton.Input active size='small' style={{ width: '100%', height: '32px' }} />
          <Skeleton.Input active size='small' style={{ width: '100%', height: '32px' }} />
        </div>
      ) : (
        <InputManager
          inputMode='edit'
          fields={fields}
          labelPosition='above'
          onChange={(key, value) => {
            setFieldValues((prev) => ({
              ...prev,
              [key]: value
            }))
          }}
        />
      )}
    </Modal>
  )
}
